import { Injectable } from '@angular/core';

import { PicksView } from '@cds/betting-offer/domain-specific';
import { BetBuilderPricing } from '@cds/betting-offer/domain-specific/bet-builder';
import { BetBuilderConfig } from '@frontend/sports/common/client-config-data-access';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { EMPTY, Observable, OperatorFunction, catchError, filter, map, of } from 'rxjs';

import { buildIPriceFromBasePrice, emptyPrice, isSuspended, mapLegInfoToClientLegInfo } from '../../../../core/group-pick-helpers';
import { BetslipGroupInformation } from '../../../../core/groups/betslip-group-information';
import { BaseBetslipGroupPick, BetslipGroupPick } from '../../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../../core/picks/betslip-pick';
import { GroupEnabledPickIds, GroupPickId, PickId } from '../../../../core/picks/pick-id';
import { IPickTracking } from '../../../../core/picks/pick-models';
import BetslipPickFactory from '../../factory/betslip-pick.factory';
import BetslipPickUtilsService from '../betslip-pick-utils.service';
import { GroupablePicks } from './groupable-picks.model';

/**
 *
 * This function appends error handling to an observable. In case of an error, it returns an EMPTY observable,
 * logs the error message to the specified logger, and adds the provided message as a signature to the logged error.
 *
 * @template T - The type of the output emitted by the observable.
 * @param {string} message - The signature message to be added to the logged error.
 * @param logger - an instance of our logger object.
 * @returns {OperatorFunction<T, T>} - The modified operator function with error handling.
 */

export function logErrorAndReturnEmpty<T>(
    message: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    logger: { error: (error: any, additionalMessage?: string) => unknown },
    onError?: Observable<T>,
): OperatorFunction<T, T> {
    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    return catchError<T, Observable<never | T>>((error) => {
        const errorObj = Object.assign(
            {},
            {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                message: error.message,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                stack: error.stack,
            },
            {
                name: message,
            },
        );

        logger.error(errorObj, message);

        return onError ? onError : EMPTY;
    });
}

interface MappedResponse {
    newPick: BetslipPick | null;
    group: BetBuilderPricing;
    fixtureData: PicksView;
}

interface MappedMultipleResponse {
    newPicks: (BetslipPick | null)[];
    group: BetBuilderPricing;
    fixtureData: PicksView;
}

@Injectable({ providedIn: 'any' })
export class GroupedPicksService {
    readonly logger: SportsRemoteLogger;

    constructor(
        loggerFactory: LoggerFactory,
        private betslipUtilsService: BetslipPickUtilsService,
        private pickFactory: BetslipPickFactory,
        private betBuilderConfig: BetBuilderConfig,
    ) {
        this.logger = loggerFactory.getLogger('GroupedPicksService');
    }

    validateAvailablePicks(availablePicks: BetslipPick[], groupInfo: BetslipGroupInformation) {
        const pickWithSameGroupInfo = this.getPickByGroupInfo(availablePicks, groupInfo);
        if (!pickWithSameGroupInfo) {
            this.logger.error('Attempting to create a group with 0 existing picks');

            return false;
        }

        return true;
    }

    /**
     * This function iterates through the provided list of availablePicks, get all picks that match the given groupInfo,
     * and returns a new group.
     * This new group includes all the existing picks that were filtered from availablePicks + the newly created pick generated from the provided pickId.
     *
     * @param isBetBuilderEnabled - Indicates if the market is bet builder enabled .
     * @param groupInfo - target group info.
     * @param pickId - The ID of the pick to be added to the group.
     * @param tracking - Tracking information for the pick.
     * @param priceId - The price ID associated with the pick.
     * @param isPriceBoosted - Indicates whether the pick has a price boost.
     * @param availablePicks - The list of available picks.
     *
     * @returns An observable containing the updated group pick and the existing group/pick  ID.
     */
    addPickToGroup(
        isBetBuilderEnabled: boolean | undefined,
        groupInfo: BetslipGroupInformation,
        pickId: GroupEnabledPickIds,
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        availablePicks: BetslipPick[],
    ): Observable<BaseBetslipGroupPick> {
        const existingGroupPick = this.getPickByGroupInfo(availablePicks, groupInfo);
        if (!existingGroupPick) {
            this.logger.error(`Can't instantiate a group, no existing picks from the same group were found in the betslip.`);

            return EMPTY;
        }
        const isValid = this.isValid(isBetBuilderEnabled, groupInfo, availablePicks);
        //calculating the nr of legs:
        //- if GroupPick we're taking the nr of legs
        //- if regular pick we're taking the count as 1.
        //to the above we're adding 1 - the new pick that's being added.
        const nrOfLegs = (BaseBetslipGroupPick.isPick(existingGroupPick) ? existingGroupPick.getLegs().length : 1) + 1;
        const overMaxNrOfLegs = this.isOverMaxNrOfLegs(nrOfLegs);
        //check if bet builder is enabled and we're not over MaxNrOfLegs, if not, update the pick with empty price and flag the pick without calling cds.
        if (isValid && !overMaxNrOfLegs) {
            //bet builder enabled for this market, fetch group info (all picks) and build valid group pick
            return this.getValidGroup(pickId, tracking, priceId, isPriceBoosted, existingGroupPick);
        } else {
            //bet builder disabled for this market (exclude other picks from cds call, set the price to 0 and remove channel id)
            return this.getInvalidGroup(pickId, tracking, priceId, isPriceBoosted, existingGroupPick);
        }
    }

    getGroupPlaceholder(
        availablePicks: BetslipPick[],
        groupInfo: BetslipGroupInformation,
    ): {
        groupPick: BaseBetslipGroupPick;
        existingPick: BetslipPick;
    } | null {
        const currentPick = this.getPickByGroupInfo(availablePicks, groupInfo);
        if (!currentPick) {
            return null;
        }
        if (BaseBetslipGroupPick.isPick(currentPick)) {
            return {
                groupPick: currentPick,
                existingPick: currentPick,
            };
        } else {
            return {
                groupPick: new BetslipGroupPick({
                    groupInfo: currentPick.groupInfo!,
                    picks: [currentPick],
                    ...this.getDefaultInvalidGroupInput(),
                }),
                existingPick: currentPick,
            };
        }
    }

    getPickByGroupInfo(availablePicks: BetslipPick[], groupInfo: BetslipGroupInformation): BetslipPick | undefined {
        return availablePicks.find((t) => t.groupInfo?.groupId === groupInfo.groupId);
    }

    /**
     * @description Performs an API call in order to fetch the latest groupPick data.
     * @returns the new pick(to replace with). The sgpId of these picks can be different!
     */
    getGroupPick(groupPick: BaseBetslipGroupPick): Observable<BaseBetslipGroupPick> {
        return this.betslipUtilsService.loadBetBuilderPricingGroupsForPicks(groupPick.getLegs()).pipe(
            map((betBuilderPricingGroups) => this.buildGroupInfoFromPicksView(betBuilderPricingGroups, groupPick.groupInfo)),
            map(
                (betBuilderPricing) =>
                    new BetslipGroupPick({
                        sgpId: betBuilderPricing.sgpId,
                        price: buildIPriceFromBasePrice(betBuilderPricing.odds),
                        legsInfo: mapLegInfoToClientLegInfo(betBuilderPricing.legInformation),
                        isSuspended: isSuspended(betBuilderPricing.suspensionState),
                        groupInfo: groupPick.groupInfo,
                        picks: groupPick.getLegs(),
                    }),
            ),
        );
    }

    addGroup(
        groupInfo: BetslipGroupInformation,
        pickIds: GroupEnabledPickIds[],
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        availablePicks: BetslipPick[],
    ): Observable<{ groupPick: BaseBetslipGroupPick; existingPickId?: PickId }> {
        const existingGroupPick = this.getPickByGroupInfo(availablePicks, groupInfo);
        if (!existingGroupPick) {
            return this.getValidPicksAddAction(pickIds, tracking, priceId, isPriceBoosted, groupInfo);
        }

        return this.getValidMultiplePicksGroupReplaceAction(pickIds, tracking, priceId, isPriceBoosted, existingGroupPick);
    }

    validateRemovablePick(picks: BetslipPick[], picksIds: GroupEnabledPickIds[], groupInfo: BetslipGroupInformation): boolean {
        const group = picks.find((t) => t.groupInfo?.groupId === groupInfo.groupId);
        if (group && BaseBetslipGroupPick.isPick(group)) {
            const legIds = group.getLegs().map((t) => t.id.toString());
            if (!picksIds.every((id) => legIds.find((t) => t === id.toString()))) {
                this.logger.error("An Attempt To Remove A Pick from a group failed, pick doesn't belong to the group");

                return false;
            }

            return true;
        } else {
            this.logger.error(`An Attempt To Remove A Pick from a group failed, GROUP wasn't found`);

            return false;
        }
    }

    getAvailableGroupsFromPicks(existingGroups: GroupablePicks[]): Observable<BetslipGroupPick[]> {
        const validGroupsPicks = existingGroups.filter((t) => t.isValid).flatMap((t) => t.picks);

        if (!validGroupsPicks.length) {
            return of(this.buildGroupsFromDefaultValues(existingGroups));
        }

        return this.betslipUtilsService.loadBetBuilderPricingGroupsForPicks(validGroupsPicks).pipe(
            map((betBuilderPricingGroups) => this.buildGroupsFromPicksView(existingGroups, betBuilderPricingGroups)),
            logErrorAndReturnEmpty(
                'getAvailableGroupsFromPicks failed, please check error stack trace for more info',
                this.logger,
                of(this.buildGroupsFromDefaultValues(existingGroups)),
            ),
        );
    }

    removePicksFromGroup(group: BetslipGroupPick, pickIds: GroupEnabledPickIds[]): Observable<BetslipPick> {
        if (group.getLegs().length - pickIds.length < 2) {
            return of(
                group.removePicks({
                    ids: pickIds,
                    sgpId: group.sgpId!,
                    price: group.currentPrice!,
                    isSuspended: group.isSuspended,
                    legsInfo: Object.values(group.legsState),
                }),
            );
        } else {
            const remainingPicks = group.getLegs().filter((p) => !pickIds.some((inputPick) => p.id.toString() === inputPick.toString()));
            const anyRemainingPickNotCombinable = remainingPicks.some((pick) => !this.isCombinablePick(pick));
            const overMaxNrOfLegs = this.isOverMaxNrOfLegs(remainingPicks.length);
            if (anyRemainingPickNotCombinable || overMaxNrOfLegs) {
                return of(
                    new BetslipGroupPick({
                        ...this.getDefaultInvalidGroupInput(),
                        groupInfo: group.groupInfo,
                        picks: remainingPicks,
                    }),
                );
            }

            return this.betslipUtilsService.loadBetBuilderPricingGroupsForPicks(remainingPicks).pipe(
                map((betBuilderPricingGroups) => {
                    const { sgpId, odds, suspensionState, legInformation } = this.buildGroupInfoFromPicksView(
                        betBuilderPricingGroups,
                        group.groupInfo,
                    );

                    return group.removePicks({
                        ids: pickIds,
                        sgpId,
                        price: buildIPriceFromBasePrice(odds),
                        isSuspended: isSuspended(suspensionState),
                        legsInfo: mapLegInfoToClientLegInfo(legInformation),
                    });
                }),
                logErrorAndReturnEmpty(
                    'removePicksFromGroup, please check error stack trace for more info ',
                    this.logger,
                    of(
                        group.removePicks({
                            ...this.getDefaultInvalidGroupInput(),
                            ids: pickIds,
                        }),
                    ),
                ),
            );
        }
    }

    splitGroupsIntoSingles(picks: BetslipPick[]): { groupId: GroupPickId; picks: BetslipPick[] }[] {
        return picks
            .filter((p) => BaseBetslipGroupPick.isPick(p))
            .map((p) => p as BaseBetslipGroupPick)
            .map((g) => ({ groupId: g.id, picks: g.getLegs() }));
    }

    getAvailablePicksForGrouping(picks: BetslipPick[]): GroupablePicks[] {
        const allPicksDictionary = picks
            .filter((y) => !BaseBetslipGroupPick.isPick(y))
            .filter((y) => !!y.groupInfo?.groupId)
            .reduce<Record<string, GroupablePicks>>(
                (acc, curr) => {
                    const groupId = curr.groupInfo!.groupId;
                    if (acc[groupId]) {
                        acc[groupId].picks.push(curr);
                        acc[groupId].isValid &&= this.isCombinablePick(curr);
                    } else {
                        acc[groupId] = {
                            groupInfo: curr.groupInfo!,
                            picks: [curr],
                            isValid: this.isCombinablePick(curr),
                        };
                    }

                    acc[groupId].isValid &&= !this.isOverMaxNrOfLegs(acc[groupId].picks.length);

                    return acc;
                },
                {} as Record<string, GroupablePicks>,
            );

        return Object.keys(allPicksDictionary)
            .filter((key) => allPicksDictionary[key].picks.length > 1)
            .flatMap((key) => allPicksDictionary[key]);
    }

    buildGroupsFromPicksView(possibleGroups: GroupablePicks[], betBuilderPricingGroups: { [key: string]: BetBuilderPricing }): BetslipGroupPick[] {
        return possibleGroups.map((groupablePicks) => {
            if (groupablePicks.isValid) {
                const { sgpId, odds, suspensionState, legInformation } = this.buildGroupInfoFromPicksView(
                    betBuilderPricingGroups,
                    groupablePicks.groupInfo,
                );

                return new BetslipGroupPick({
                    groupInfo: groupablePicks.groupInfo,
                    sgpId,
                    picks: groupablePicks.picks,
                    price: buildIPriceFromBasePrice(odds),
                    isSuspended: isSuspended(suspensionState),
                    legsInfo: mapLegInfoToClientLegInfo(legInformation),
                });
            } else {
                return this.buildGroupFromDefaultValues(groupablePicks);
            }
        });
    }

    buildGroupsFromDefaultValues(possibleGroups: GroupablePicks[]): BetslipGroupPick[] {
        return possibleGroups.map((possibleGroup) => this.buildGroupFromDefaultValues(possibleGroup));
    }

    buildGroupFromDefaultValues(groupablePicks: GroupablePicks): BetslipGroupPick {
        return new BetslipGroupPick({
            ...this.getDefaultInvalidGroupInput(),
            groupInfo: groupablePicks.groupInfo,
            picks: groupablePicks.picks,
        });
    }

    //This is used in case the pick that need to be added to the group is supporting groups
    private getValidGroup(
        pickId: GroupEnabledPickIds,
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        existingGroupPick: BetslipPick,
    ): Observable<BaseBetslipGroupPick> {
        const picksIds = BaseBetslipGroupPick.isPick(existingGroupPick) ? existingGroupPick.getLegs().map((p) => p.id) : [existingGroupPick.id];
        let updatedTracking = { ...tracking };
        if (existingGroupPick.tracking?.sourceOfBetSuffix) {
            updatedTracking = { ...tracking, sourceOfBetSuffix: existingGroupPick.tracking.sourceOfBetSuffix };
        }

        return this.betslipUtilsService.loadPicksFromPickIds([pickId, ...picksIds], existingGroupPick.groupInfo!).pipe(
            map((pickView) => this.mapPicksViewToGroupPick(pickId, updatedTracking, priceId, isPriceBoosted, pickView, existingGroupPick.groupInfo!)),
            map((response) => {
                if (this.validateMappedGroup(response)) {
                    return this.mapSuccessfulResponseToReplaceAction(existingGroupPick, response.newPick!, response.group);
                }

                return this.buildGroupWithInvalidPick(existingGroupPick, response.newPick!);
            }),

            logErrorAndReturnEmpty('getValidGroup, please check error stack trace for more info ', this.logger),
        );
    }

    private getValidMultiplePicksGroupReplaceAction(
        pickIds: GroupEnabledPickIds[],
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        existingGroupPick: BetslipPick,
    ): Observable<{ groupPick: BaseBetslipGroupPick; existingPickId?: PickId }> {
        const picksIds = existingGroupPick instanceof BaseBetslipGroupPick ? existingGroupPick.getLegs().map((p) => p.id) : [existingGroupPick.id];

        return this.betslipUtilsService.loadPicksFromPickIds([...pickIds, ...picksIds], existingGroupPick.groupInfo!).pipe(
            map((pickView) =>
                this.mapMultiplePicksViewToGroupPick(pickIds, tracking, priceId, isPriceBoosted, pickView, existingGroupPick.groupInfo!),
            ),
            filter((x) => this.validateMultipleMappedGroup(x)),
            map(({ newPicks, group }) =>
                this.mapSuccessfulResponseMultipleToReplaceAction(
                    existingGroupPick,
                    newPicks.flatMap((pick) => pick || []),
                    group,
                ),
            ),
            logErrorAndReturnEmpty('getValidMultiplePicksGroupReplaceAction, please check error stack trace for more info ', this.logger),
        );
    }

    private getValidPicksAddAction(
        pickIds: GroupEnabledPickIds[],
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        groupInfo: BetslipGroupInformation,
    ) {
        return this.betslipUtilsService.loadPicksFromPickIds(pickIds, groupInfo).pipe(
            map((pickView) => this.mapMultiplePicksViewToGroupPick(pickIds, tracking, priceId, isPriceBoosted, pickView, groupInfo)),
            map(({ newPicks, group }) => this.mapSuccessfulResponseToAddAction(newPicks, group, groupInfo)),
            logErrorAndReturnEmpty('getValidPicksAddAction, please check error stack trace for more info ', this.logger),
        );
    }

    private mapSuccessfulResponseToAddAction(newPicks: (BetslipPick | null)[], group: BetBuilderPricing, groupInfo: BetslipGroupInformation) {
        const newPicksNotNull: BetslipPick[] = [];
        newPicks.map((p) => {
            if (p !== null) {
                newPicksNotNull.push(p);
            }
        });

        return {
            groupPick: new BetslipGroupPick({
                groupInfo,
                sgpId: group.sgpId,
                picks: newPicksNotNull,
                price: buildIPriceFromBasePrice(group.odds),
                legsInfo: mapLegInfoToClientLegInfo(group.legInformation),
                isSuspended: isSuspended(group.suspensionState),
            }),
        };
    }

    //This is used in case we need to create a group without a price call(eg. nonSGPable legs, overMaxNrOfLegs in the group)
    private getInvalidGroup(
        pickId: GroupEnabledPickIds,
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        existingGroupPick: BetslipPick,
    ): Observable<BaseBetslipGroupPick> {
        return this.betslipUtilsService.loadPickFromApi(pickId).pipe(
            map((enhancedFixture) =>
                this.pickFactory.createPick(
                    pickId,
                    tracking,
                    enhancedFixture.fixture,
                    priceId,
                    isPriceBoosted,
                    undefined,
                    existingGroupPick.groupInfo,
                ),
            ),
            map((newPick) => this.buildGroupWithInvalidPick(existingGroupPick, newPick!)),
            logErrorAndReturnEmpty('getInvalidGroup, please check error stack trace for more info', this.logger),
        );
    }

    private mapPicksViewToGroupPick(
        pickId: GroupEnabledPickIds,
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        pickView: PicksView,
        groupInfo: BetslipGroupInformation,
    ): MappedResponse {
        return {
            newPick: this.pickFactory.createPick(
                pickId,
                tracking,
                pickView.fixturePage.fixtures.find((f) => f.id === pickId.fixtureId)!,
                priceId,
                isPriceBoosted,
                undefined,
                groupInfo,
            ),
            group: this.buildGroupInfoFromPicksView(pickView.betBuilderPricingGroups, {
                groupId: groupInfo.groupId,
                provider: groupInfo.provider,
            }),
            fixtureData: pickView,
        };
    }

    private mapMultiplePicksViewToGroupPick(
        pickIds: GroupEnabledPickIds[],
        tracking: IPickTracking,
        priceId: number | undefined,
        isPriceBoosted: boolean | undefined,
        pickView: PicksView,
        groupInfo: BetslipGroupInformation,
    ) {
        return {
            newPicks: pickIds.map((pickId) =>
                this.pickFactory.createPick(
                    pickId,
                    tracking,
                    pickView.fixturePage.fixtures.find((f) => f.id === pickId.fixtureId)!,
                    priceId,
                    isPriceBoosted,
                    undefined,
                    groupInfo,
                ),
            ),
            group: this.buildGroupInfoFromPicksView(pickView.betBuilderPricingGroups, { groupId: groupInfo.groupId, provider: groupInfo.provider }),
            fixtureData: pickView,
        };
    }

    private validateMappedGroup({ newPick, group, fixtureData }: MappedResponse): boolean {
        if (!newPick) {
            this.logger.error('Pick Factory Failed To Create The Pick');

            return false;
        }
        if (!group) {
            const error = JSON.stringify({ message: `An Attempt To Add A New Group failed, GROUP wasn't returned in CDS response`, fixtureData });

            this.logger.error(error);

            return false;
        }

        return true;
    }

    private validateMultipleMappedGroup({ newPicks, group, fixtureData }: MappedMultipleResponse): boolean {
        if (newPicks.some((pick) => pick === null)) {
            this.logger.error('Pick Factory Failed To Create The Pick');

            return false;
        }

        if (!fixtureData.betBuilderPricingGroups) {
            this.logger.error('Missing betBuilderPricingGroups from CDS response');

            return false;
        }

        if (!group) {
            const error = JSON.stringify({ message: `An Attempt To Add A New Group failed, GROUP wasn't returned in CDS response`, fixtureData });

            this.logger.error(error);

            return false;
        }

        return true;
    }

    private mapSuccessfulResponseToReplaceAction(existingGroupPick: BetslipPick, pick: BetslipPick, group: BetBuilderPricing) {
        const replaceGroupInput = {
            sgpId: group.sgpId,
            price: buildIPriceFromBasePrice(group.odds),
            legsInfo: mapLegInfoToClientLegInfo(group.legInformation),
            isSuspended: isSuspended(group.suspensionState),
        };

        let newPick: BaseBetslipGroupPick;
        if (BaseBetslipGroupPick.isPick(existingGroupPick)) {
            newPick = existingGroupPick.addPick({ ...replaceGroupInput, pick });
        } else {
            newPick = new BetslipGroupPick({
                ...replaceGroupInput,
                groupInfo: existingGroupPick.groupInfo!,
                picks: [existingGroupPick, pick],
            });
        }

        return newPick;
    }

    private mapSuccessfulResponseMultipleToReplaceAction(existingGroupPick: BetslipPick, newPicks: BetslipPick[], group: BetBuilderPricing) {
        let newPick: BaseBetslipGroupPick;
        const groupPickInputs = {
            sgpId: group.sgpId,
            price: buildIPriceFromBasePrice(group.odds),
            legsInfo: mapLegInfoToClientLegInfo(group.legInformation),
            isSuspended: isSuspended(group.suspensionState),
        };

        if (existingGroupPick instanceof BaseBetslipGroupPick) {
            newPick = existingGroupPick.addPicks({ ...groupPickInputs, picks: newPicks });
        } else {
            newPick = new BetslipGroupPick({
                ...groupPickInputs,
                groupInfo: existingGroupPick.groupInfo!,
                picks: [existingGroupPick, ...newPicks.filter((pick) => !!pick)],
            });
        }

        return { groupPick: newPick, existingPickId: existingGroupPick.id };
    }

    private buildGroupWithInvalidPick(existingGroupPick: BetslipPick, newPick: BetslipPick): BaseBetslipGroupPick {
        if (BaseBetslipGroupPick.isPick(existingGroupPick)) {
            const newGroup = existingGroupPick.addPick({
                ...this.getDefaultInvalidGroupInput(),
                pick: newPick,
                isSuspended: existingGroupPick.isSuspended,
                legsInfo: Object.values(existingGroupPick.legsState),
            });

            return newGroup;
        } else {
            const newGroup = new BetslipGroupPick({
                groupInfo: existingGroupPick.groupInfo!,
                picks: [existingGroupPick, newPick],
                ...this.getDefaultInvalidGroupInput(),
            });

            return newGroup;
        }
    }

    private buildGroupInfoFromPicksView(
        betBuilderPricingGroups: { [key: string]: BetBuilderPricing },
        group: BetslipGroupInformation,
    ): BetBuilderPricing {
        return Object.values(betBuilderPricingGroups).find((pricingGroup) => pricingGroup.groupId === group.groupId)!;
    }

    private isValid(isBetBuilderEnabled: boolean | undefined, groupInfo: BetslipGroupInformation, availablePicks: BetslipPick[]): boolean {
        if (!isBetBuilderEnabled) {
            return false;
        }

        const sameGroupPick = availablePicks.find((t) => !!t.groupInfo && t.groupInfo.groupId === groupInfo.groupId);
        if (sameGroupPick && BaseBetslipGroupPick.isPick(sameGroupPick)) {
            //if we already have a group, we check if that group has uncombinable picks
            return !sameGroupPick.groupHasUncombinableLeg() && !sameGroupPick.isLive;
        }

        //if we have a "regular" pick with the same groupInfo, we check if we can combine it
        return !!sameGroupPick && this.isCombinablePick(sameGroupPick);
    }

    private getDefaultInvalidGroupInput() {
        return {
            sgpId: '',
            price: buildIPriceFromBasePrice(emptyPrice()),
            isSuspended: false,
            legsInfo: [],
        };
    }

    private isCombinablePick(pick: BetslipPick): boolean {
        return pick.market.isBetBuilderEnabled && !pick.isLive;
    }

    private isOverMaxNrOfLegs(legsCount: number): boolean {
        return legsCount > this.betBuilderConfig.maxSelectionsPerPick;
    }
}
